// src/components/TaskTimeByPerson.jsx
//
// How one task's time splits between the people who worked it.
//
// A task that passes through three people reports one number, and one number
// cannot say that Mano spent 10 minutes, Anbu 5 and Surya 3. The tracker has
// always known — every status change records who made it — so this is a read,
// not new bookkeeping.
//
// It fetches for itself rather than being handed its rows. The dialog it sits
// in is opened one task at a time and the two screens that show that dialog
// would otherwise both need the same fetch, the same loading flag and the same
// cleanup. Nothing else on either screen wants this data.
import React, { useEffect, useState } from "react";

import api from "../utils/api";
import { apiPath } from "../utils/apiPath";
import { formatDateTimeDisplay, secondsToHoursMinutes } from "../utils/common";

/* The clock keeps running on whoever holds the task right now, so their figure
   is the banked stretches plus the one in flight. Ticked here rather than left
   to a refresh, because a number that is quietly a few minutes stale is worse
   than no number when the whole point is measuring time. */
const useNow = (active) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);

  return now;
};

const TaskTimeByPerson = ({ queryCode }) => {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("idle");

  useEffect(() => {
    if (!queryCode) return undefined;

    let alive = true;
    setState("loading");

    api
      .get(`${apiPath.API_URL}${apiPath.tasks.workedTimeByUser}`, {
        params: { queryCode },
      })
      .then((res) => {
        if (!alive) return;
        /* successHandler wraps once and axios wraps again, and which level
           carries the rows has differed by endpoint before. */
        const body = res?.data?.data ?? res?.data ?? [];
        setRows(Array.isArray(body) ? body : []);
        setState("done");
      })
      .catch(() => {
        if (!alive) return;
        setRows([]);
        setState("failed");
      });

    /* The dialog can be closed, or another task opened, while this is in
       flight — without this the late answer paints one task's split under
       another task's heading. */
    return () => {
      alive = false;
    };
  }, [queryCode]);

  const running = rows.find((r) => r.IsRunning && r.ActiveStartedAt);
  const now = useNow(!!running);

  if (state === "loading") {
    return <div className="tbp__note">Reading the time…</div>;
  }

  if (state === "failed") {
    return <div className="tbp__note">The time breakdown could not be read.</div>;
  }

  if (!rows.length) {
    return (
      <div className="tbp__note">
        Nobody has started this task yet, so there is no time to split.
      </div>
    );
  }

  /* Live seconds for whoever holds it, added on top of their banked stretches.
     The server deliberately does not count the open stretch — it would be
     stale the moment it was sent — so the addition happens here. */
  const liveFor = (r) => {
    if (!r.IsRunning || !r.ActiveStartedAt) return 0;
    const since = new Date(r.ActiveStartedAt).getTime();
    const secs = Math.floor((now - since) / 1000);
    return secs > 0 ? secs : 0;
  };

  const totalFor = (r) => (Number(r.WorkedSeconds) || 0) + liveFor(r);
  const total = rows.reduce((sum, r) => sum + totalFor(r), 0);

  return (
    <div className="tbp">
      <table className="tbp__table">
        <tbody>
          {rows.map((r) => (
            <tr key={r.UserCode ?? r.UserName} className={r.IsRunning ? "is-running" : ""}>
              <td className="tbp__who">
                {r.UserName}
                {r.IsRunning && <span className="tbp__live">running</span>}
              </td>
              <td className="tbp__when">
                {/* What they did, not just how long — a person with four short
                    stretches spread over a week worked the task differently
                    from one who sat with it for an afternoon. */}
                {r.Stretches > 1 && (
                  <span className="tbp__stretches">{r.Stretches} sittings</span>
                )}
                {r.FirstStartedAt && (
                  <span className="tbp__first">
                    from {formatDateTimeDisplay(r.FirstStartedAt, "DD MMM, hh:mm A", "")}
                  </span>
                )}
              </td>
              <td className="tbp__time">{secondsToHoursMinutes(totalFor(r))}</td>
            </tr>
          ))}
        </tbody>

        {/* The parts add up to the whole, and the whole is shown so that can be
            seen rather than taken on trust — the procedure is written against
            the same arithmetic the task total uses, precisely so these agree. */}
        {rows.length > 1 && (
          <tfoot>
            <tr>
              <td className="tbp__who">Total</td>
              <td />
              <td className="tbp__time">{secondsToHoursMinutes(total)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default TaskTimeByPerson;
